import { changed, noOp, rejected, type Command, type CommandExecution, type LiveCommand } from './commands';
import { damage } from './damage';
import { findLayer, type AdjustmentLayer, type EditorDocument, type LayerId } from './model';
import type { ToneCurvePoint, ToneCurves } from '../filters/tone';

export type CurveChannel = keyof ToneCurves;

function curveValue(points: readonly ToneCurvePoint[], input: number): number {
  const normalized = [{ x: 0, y: 0 }, ...points, { x: 1, y: 1 }]
    .map((point) => ({ x: Math.max(0, Math.min(1, point.x)), y: Math.max(0, Math.min(1, point.y)) }))
    .sort((left, right) => left.x - right.x);
  const endIndex = normalized.findIndex((point) => point.x >= input);
  const end = normalized[Math.max(1, endIndex)] ?? normalized.at(-1)!;
  const start = normalized[Math.max(0, (endIndex < 0 ? normalized.length - 1 : endIndex) - 1)] ?? normalized[0]!;
  const amount = (input - start.x) / Math.max(Number.EPSILON, end.x - start.x);
  return start.y + (end.y - start.y) * Math.max(0, Math.min(1, amount));
}

function curvesWithPoint(curves: ToneCurves | undefined, channel: CurveChannel, input: number, output: number): ToneCurves {
  const current: ToneCurves = curves ?? { rgb: [], red: [], green: [], blue: [] };
  return {
    rgb: [...current.rgb],
    red: [...current.red],
    green: [...current.green],
    blue: [...current.blue],
    [channel]: [...current[channel].filter((point) => Math.abs(point.x - input) > 1 / 510), { x: input, y: output }].sort((left, right) => left.x - right.x),
  };
}

export class TargetedCurveCommand implements LiveCommand {
  readonly label = 'Targeted Curves adjustment';
  private before?: AdjustmentLayer['adjustment'];
  private after?: AdjustmentLayer['adjustment'];
  private readonly input: number;
  private initialOutput = 0;
  private moved = false;

  constructor(private readonly layerId: LayerId, private readonly channel: CurveChannel, sampledInput: number) {
    this.input = Math.round(Math.max(0, Math.min(1, sampledInput)) * 255) / 255;
  }

  retainedBytes(): number {
    return 256 + JSON.stringify(this.before ?? {}).length * 2 + JSON.stringify(this.after ?? {}).length * 2;
  }

  execute(document: EditorDocument): CommandExecution {
    const layer = findLayer(document.root, this.layerId);
    if (layer?.kind !== 'adjustment') return rejected('Adjustment layer no longer exists.');
    this.before ??= structuredClone(layer.adjustment);
    this.initialOutput = curveValue(layer.adjustment.curves?.[this.channel] ?? [], this.input);
    layer.adjustment.curves = curvesWithPoint(layer.adjustment.curves, this.channel, this.input, this.initialOutput);
    return changed(this.damage());
  }

  update(document: EditorDocument, delta: number): void {
    const layer = findLayer(document.root, this.layerId);
    if (layer?.kind !== 'adjustment' || !this.before) return;
    const output = Math.max(0, Math.min(1, this.initialOutput + delta));
    layer.adjustment.curves = curvesWithPoint(this.before.curves, this.channel, this.input, output);
    this.moved ||= Math.abs(output - this.initialOutput) >= 1 / 510;
  }

  seal(document: EditorDocument): CommandExecution {
    const layer = findLayer(document.root, this.layerId);
    if (layer?.kind !== 'adjustment') return rejected('Adjustment layer no longer exists.');
    if (!this.moved) {
      this.undo(document);
      return noOp();
    }
    this.after = structuredClone(layer.adjustment);
    return changed(this.damage());
  }

  undo(document: EditorDocument): void {
    const layer = findLayer(document.root, this.layerId);
    if (layer?.kind === 'adjustment' && this.before) layer.adjustment = structuredClone(this.before);
  }

  redo(document: EditorDocument): void {
    const layer = findLayer(document.root, this.layerId);
    if (layer?.kind === 'adjustment' && this.after) layer.adjustment = structuredClone(this.after);
  }

  damage() { return damage({ layerIds: [this.layerId], fullDocument: true }); }
}

/**
 * The committed result of one Targeted Curves drag, from data: the point at
 * the sampled `input` (rounded to 1/255, as the gesture does) moves to
 * `output`. Byte-identical to a `TargetedCurveCommand` gesture whose delta is
 * `output` minus the curve's current value at `input`; a point that would not
 * move (less than half a level) is a no-op, like an unmoved drag.
 */
export class SetTargetedCurvePointCommand implements Command {
  readonly label = 'Targeted Curves adjustment';
  private before?: AdjustmentLayer['adjustment'];
  private after?: AdjustmentLayer['adjustment'];
  private readonly input: number;
  private readonly output: number;

  constructor(private readonly layerId: LayerId, private readonly channel: CurveChannel, sampledInput: number, output: number) {
    this.input = Math.round(Math.max(0, Math.min(1, sampledInput)) * 255) / 255;
    this.output = Math.max(0, Math.min(1, output));
  }

  retainedBytes(): number {
    return 256 + JSON.stringify(this.before ?? {}).length * 2 + JSON.stringify(this.after ?? {}).length * 2;
  }

  execute(document: EditorDocument): CommandExecution {
    const layer = findLayer(document.root, this.layerId);
    if (layer?.kind !== 'adjustment') return rejected('Adjustment layer no longer exists.');
    if (this.after) { layer.adjustment = structuredClone(this.after); return changed(this.damage()); }
    const initial = curveValue(layer.adjustment.curves?.[this.channel] ?? [], this.input);
    if (Math.abs(this.output - initial) < 1 / 510) return noOp();
    this.before = structuredClone(layer.adjustment);
    layer.adjustment.curves = curvesWithPoint(this.before.curves, this.channel, this.input, this.output);
    this.after = structuredClone(layer.adjustment);
    return changed(this.damage());
  }

  undo(document: EditorDocument): void {
    const layer = findLayer(document.root, this.layerId);
    if (layer?.kind === 'adjustment' && this.before) layer.adjustment = structuredClone(this.before);
  }

  damage() { return damage({ layerIds: [this.layerId], fullDocument: true }); }
}
